import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './VideoProcessing.css';

function VideoProcessing({ video, onProcessingComplete, moderatorAnnotations, autoStart = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [waitingForModerator, setWaitingForModerator] = useState(false);
  const [waitingForBackendProcessing, setWaitingForBackendProcessing] = useState(false);
  const [detectedIssues, setDetectedIssues] = useState([]);
  const [showDetectedIssues, setShowDetectedIssues] = useState(false);
  const [processingSpeed, setProcessingSpeed] = useState(1); // Base processing speed multiplier
  const processingIntervalRef = useRef(null);
  const pausePointRef = useRef(50); // Default pause point, will be randomized

  // Define processing steps
  const processingSteps = [
    {
      name: "Loading AI Models",
      subSteps: ["YOLOv5 Detection Model", "Smoking Recognition Model", "Violence Detection Model", "No Helmet Detection Model"],
      duration: 5000,
      complexityFactor: 0.8 // Less affected by video complexity
    },
    {
      name: "Extracting Video Frames",
      subSteps: ["Decoding video stream", "Extracting key frames", "Preparing for analysis"],
      duration: 8000,
      complexityFactor: 1.5 // Highly affected by video length and resolution
    },
    {
      name: "Applying Machine Learning",
      subSteps: ["Running object detection", "Analyzing content", "Identifying timestamps"],
      duration: 10000,
      complexityFactor: 1.8 // Very affected by content complexity
    },
    {
      name: "Deep Content Analysis",
      subSteps: ["Running advanced neural networks", "Performing contextual analysis", "Generating content metadata"],
      duration: 6000,
      requiresModeratorInput: true,
      complexityFactor: 1.2 // Moderately affected by content complexity
    },
    {
      name: "Content Issues Detected",
      subSteps: ["Analyzing detected issues", "Preparing moderation strategy", "Generating effect parameters"],
      duration: 8000,
      showAfterModeratorInput: true,
      complexityFactor: 1.0 // Standard complexity
    },
    {
      name: "Applying Video Effects",
      subSteps: ["Preparing blur effects", "Generating text overlays", "Applying transformations"],
      duration: 12000,
      complexityFactor: 2.0 // Highly affected by number of issues and video length
    },
    {
      name: "Finalizing Video",
      subSteps: ["Encoding processed video", "Optimizing quality", "Preparing for delivery"],
      duration: 7000,
      waitForBackendProcessing: true,
      complexityFactor: 1.7 // Highly affected by video length and quality
    }
  ];

  // Generate a random pause point for Deep Content Analysis when component mounts
  useEffect(() => {
    // Generate a random pause point between 35% and 75%
    pausePointRef.current = Math.floor(35 + Math.random() * 40);
  }, []);

  // Auto-start processing if annotations are available
  useEffect(() => {
    if (autoStart && moderatorAnnotations && moderatorAnnotations.videoId === video._id && !isProcessing && !processingComplete) {
      // If the video is already annotated, start from Deep Content Analysis step
      setIsProcessing(true);
      setCurrentStep(3); // Deep Content Analysis step
      setProgress(pausePointRef.current); // Start from the dynamic pause point
      
      // Process the annotations
      processAnnotations(moderatorAnnotations.annotations);
    }
  }, [autoStart, moderatorAnnotations, video._id, isProcessing, processingComplete]);

  // Check if video has been annotated by moderator (hidden from user)
  useEffect(() => {
    if (waitingForModerator) {
      const interval = setInterval(async () => {
        try {
          const response = await api.get(`/videos/${video._id}`);
          const updatedVideo = response.data;
          
          // If video status has changed to Annotated, continue processing
          if (updatedVideo.status === 'Annotated') {
            clearInterval(interval);
            
            // Extract annotations to show what was "detected"
            processAnnotations(updatedVideo.annotations);
          } else {
            // Occasionally show small progress fluctuations to simulate ongoing analysis
            if (Math.random() < 0.3) {
              setProgress(prev => {
                // Small random fluctuation around the pause point (±2%)
                const fluctuation = (Math.random() * 4) - 2;
                const newProgress = pausePointRef.current + fluctuation;
                // Keep within reasonable bounds
                return Math.max(pausePointRef.current - 2, Math.min(newProgress, pausePointRef.current + 2));
              });
            }
          }
        } catch (error) {
          console.error('Error checking video status:', error);
        }
      }, 3000); // Check every 3 seconds
      
      // Cleanup interval on unmount
      return () => clearInterval(interval);
    }
  }, [waitingForModerator, video._id]);

  // Check if backend processing is complete
  useEffect(() => {
    if (waitingForBackendProcessing) {
      const interval = setInterval(async () => {
        try {
          const response = await api.get(`/videos/${video._id}`);
          const updatedVideo = response.data;
          
          // If video status has changed to Processed, continue processing
          if (updatedVideo.status === 'Processed' && updatedVideo.processedFilePath) {
            clearInterval(interval);
            setWaitingForBackendProcessing(false);
            
            // Complete the final step
            await runSingleStep(currentStep, false, 50); // Continue from 50%
            setProcessingComplete(true);
            
            if (onProcessingComplete) {
              onProcessingComplete();
            }
          } else {
            // Simulate backend processing progress by randomly adjusting the progress
            // This creates the illusion of real processing happening
            setProgress(prev => {
              // Occasionally show progress moving forward
              if (Math.random() < 0.4) {
                // Calculate a new progress value between current and 50
                const maxProgress = 50;
                const increment = Math.random() * 2; // Random increment between 0-2%
                return Math.min(prev + increment, maxProgress);
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('Error checking video processing status:', error);
        }
      }, 3000); // Check every 3 seconds
      
      // Cleanup interval on unmount
      return () => clearInterval(interval);
    }
  }, [waitingForBackendProcessing, video._id, currentStep, onProcessingComplete]);

  // Process annotations from moderator
  const processAnnotations = (annotations) => {
    const issues = [];
    annotations.forEach(annotation => {
      annotation.incidentTypes.forEach(type => {
        const timeFormatted = formatTimeRange(annotation.startTime, annotation.endTime);
        issues.push({
          type,
          timeRange: timeFormatted
        });
      });
    });
    
    setDetectedIssues(issues);
    setShowDetectedIssues(true);
    
    // Adjust processing speed based on number of issues
    // More issues = slower processing (more work to do)
    const issueCount = issues.length;
    const speedAdjustment = Math.max(0.7, 1 - (issueCount * 0.1)); // Reduce speed by 10% per issue, min 70% speed
    setProcessingSpeed(prev => prev * speedAdjustment);
    
    // Continue processing with the detected issues
    continueProcessingWithIssues();
  };

  // Format time in seconds to MM:SS format
  const formatTimeRange = (startTime, endTime) => {
    const formatTime = (time) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // Calculate total duration based on video length and complexity
  const calculateDuration = (baseTime, videoLength) => {
    // If we don't have video length info, use the base time with some randomness
    if (!videoLength) {
      const randomVariation = 0.8 + Math.random() * 0.4; // Random variation between 0.8-1.2
      return Math.round(baseTime * randomVariation);
    }
    
    // Get complexity factor for current step
    const currentStepComplexity = processingSteps[currentStep]?.complexityFactor || 1;
    
    // Scale the duration based on video length
    const lengthFactor = Math.max(1, Math.min(3, videoLength / 60)); // Scale between 1-3x based on minutes
    
    // Calculate content complexity factor (simulated)
    // Longer videos are assumed to have more complex content
    const contentComplexity = 0.8 + (Math.min(videoLength, 600) / 600); // 0.8-1.8 range
    
    // Apply all factors
    const adjustedDuration = baseTime * lengthFactor * currentStepComplexity * contentComplexity;
    
    // Apply current processing speed
    const speedAdjustedDuration = adjustedDuration / processingSpeed;
    
    // Add some randomness to make it feel more natural
    const randomVariation = 0.85 + Math.random() * 0.3; // Random variation between 0.85-1.15
    
    return Math.round(speedAdjustedDuration * randomVariation);
  };

  // Start processing
  const startProcessing = () => {
    // Set initial processing speed based on video characteristics
    const initialSpeed = calculateInitialProcessingSpeed();
    setProcessingSpeed(initialSpeed);
    
    // Generate a new random pause point for this processing session
    pausePointRef.current = Math.floor(35 + Math.random() * 40);
    
    setIsProcessing(true);
    runProcessingSteps();
  };
  
  // Calculate initial processing speed based on video characteristics
  const calculateInitialProcessingSpeed = () => {
    // Base speed is 1.0
    let speed = 1.0;
    
    // Adjust based on video duration if available
    if (video?.duration) {
      // Shorter videos process faster
      if (video.duration < 60) { // Less than 1 minute
        speed *= 1.3;
      } else if (video.duration > 300) { // More than 5 minutes
        speed *= 0.8;
      }
    }
    
    // Adjust based on file size if available
    if (video?.fileSize) {
      // Larger files process slower
      const fileSizeMB = video.fileSize / (1024 * 1024);
      if (fileSizeMB > 100) {
        speed *= 0.85;
      } else if (fileSizeMB < 20) {
        speed *= 1.2;
      }
    }
    
    // Add some randomness
    speed *= (0.9 + Math.random() * 0.2); // 0.9-1.1 random factor
    
    return speed;
  };

  // Run through all processing steps
  const runProcessingSteps = async () => {
    for (let i = 0; i < processingSteps.length; i++) {
      // Skip the "Content Issues Detected" step initially
      if (processingSteps[i].showAfterModeratorInput) {
        continue;
      }
      
      setCurrentStep(i);
      
      // If this step requires moderator input, pause here
      if (processingSteps[i].requiresModeratorInput) {
        // Use the dynamic pause point instead of fixed 50%
        await runSingleStep(i, true, 0, pausePointRef.current);
        setWaitingForModerator(true);
        return; // Exit and wait for moderator input
      } 
      // If this step requires waiting for backend processing
      else if (processingSteps[i].waitForBackendProcessing) {
        // Trigger the backend processing
        await api.post(`/videos/${video._id}/process`);
        
        // Generate a random pause point for backend processing (40-60%)
        const backendPausePoint = Math.floor(40 + Math.random() * 20);
        
        // Run the step to the dynamic pause point
        await runSingleStep(i, true, 0, backendPausePoint);
        
        // Wait for backend processing to complete
        setWaitingForBackendProcessing(true);
        return; // Exit and wait for backend processing
      }
      else {
        await runSingleStep(i);
      }
    }
    
    // After all steps complete, update the video status in the backend
    completeProcessing();
  };

  // Continue processing with detected issues
  const continueProcessingWithIssues = async () => {
    // Complete the current step if it was paused
    if (waitingForModerator) {
      await runSingleStep(currentStep, false, pausePointRef.current); // Start from the dynamic pause point
      setWaitingForModerator(false);
    }
    
    // Move to the "Content Issues Detected" step
    setCurrentStep(4); // Index of the "Content Issues Detected" step
    await runSingleStep(4);
    
    // Continue with remaining steps
    continueProcessingAfterIssuesDetected();
  };

  // Continue processing after issues are detected
  const continueProcessingAfterIssuesDetected = async () => {
    // Continue with remaining steps (skip the "Content Issues Detected" step which we already showed)
    for (let i = 5; i < processingSteps.length; i++) {
      setCurrentStep(i);
      
      // If this step requires waiting for backend processing
      if (processingSteps[i].waitForBackendProcessing) {
        // Trigger the backend processing
        await api.post(`/videos/${video._id}/process`);
        
        // Generate a random pause point for backend processing (40-60%)
        const backendPausePoint = Math.floor(40 + Math.random() * 20);
        
        // Run the step to the dynamic pause point
        await runSingleStep(i, true, 0, backendPausePoint);
        
        // Wait for backend processing to complete
        setWaitingForBackendProcessing(true);
        return; // Exit and wait for backend processing
      } else {
        await runSingleStep(i);
      }
    }
    
    // After all steps complete, update the video status in the backend
    completeProcessing();
  };

  // Complete the processing
  const completeProcessing = async () => {
    setProcessingComplete(true);
    if (onProcessingComplete) {
      onProcessingComplete();
    }
  };

  // Run a single processing step with dynamic speed variations
  const runSingleStep = async (stepIndex, pauseHalfway = false, startFrom = 0, pauseAt = 50) => {
    const step = processingSteps[stepIndex];
    const stepDuration = calculateDuration(step.duration, video?.duration || 0);
    let incrementInterval = stepDuration / 100;
    
    if (startFrom === 0) {
      setProgress(0);
    } else {
      setProgress(startFrom);
    }
    
    // Clear any existing interval
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
    }
    
    return new Promise((resolve) => {
      let currentProgress = startFrom;
      let lastSpeedChange = Date.now();
      let currentSpeedMultiplier = 1.0;
      
      const updateProgress = () => {
        // Occasionally change processing speed to simulate real-world processing
        const now = Date.now();
        if (now - lastSpeedChange > 1500) { // Change speed every ~1.5 seconds
          lastSpeedChange = now;
          
          // 30% chance of speed change
          if (Math.random() < 0.3) {
            // Generate a random speed multiplier between 0.5 and 1.5
            currentSpeedMultiplier = 0.5 + Math.random();
            
            // Occasionally simulate processing bottlenecks (very slow)
            if (Math.random() < 0.1) {
              currentSpeedMultiplier = 0.2 + Math.random() * 0.3; // 0.2-0.5x speed
            }
            
            // Occasionally simulate processing bursts (very fast)
            if (Math.random() < 0.1) {
              currentSpeedMultiplier = 1.5 + Math.random() * 0.5; // 1.5-2.0x speed
            }
          }
        }
        
        // Calculate progress increment based on current speed
        const increment = currentSpeedMultiplier;
        currentProgress += increment;
        
        // Update progress state
        setProgress(Math.min(currentProgress, 100));
        
        // If we need to pause at a specific point
        if (pauseHalfway && currentProgress >= pauseAt) {
          clearInterval(processingIntervalRef.current);
          resolve();
          return;
        }
        
        if (currentProgress >= 100) {
          clearInterval(processingIntervalRef.current);
          resolve();
        }
      };
      
      // Start the interval
      processingIntervalRef.current = setInterval(updateProgress, incrementInterval);
    });
  };

  // Get current step info
  const getCurrentStep = () => {
    return processingSteps[currentStep] || processingSteps[0];
  };

  // Get current sub-step based on progress
  const getCurrentSubStep = () => {
    const step = getCurrentStep();
    const subStepCount = step.subSteps.length;
    
    // If waiting for moderator, always show the first sub-step
    if (waitingForModerator) {
      return step.subSteps[0];
    }
    
    const subStepIndex = Math.min(
      Math.floor((progress / 100) * subStepCount),
      subStepCount - 1
    );
    return step.subSteps[subStepIndex];
  };

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="video-processing-container">
      {!isProcessing && !processingComplete ? (
        <div className="processing-start">
          <h3>Ready to Process Video</h3>
          <p>Our AI will analyze this video for content that requires moderation.</p>
          <button 
            className="btn btn-primary processing-btn"
            onClick={startProcessing}
          >
            <i className="fas fa-cogs"></i> Process Video
          </button>
        </div>
      ) : processingComplete ? (
        <div className="processing-complete">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h3>Processing Complete!</h3>
          <p>Your video has been successfully processed and is ready to view.</p>
        </div>
      ) : (
        <div className="processing-steps">
          <h3>Processing Video</h3>
          
          <div className="current-step">
            <div className="step-header">
              <span className="step-number">{currentStep + 1}/{processingSteps.length - (showDetectedIssues ? 0 : 1)}</span>
              <h4>{getCurrentStep().name}</h4>
            </div>
            
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            
            <div className="step-details">
              <p className="sub-step">
                <i className="fas fa-spinner fa-spin"></i> {getCurrentSubStep()}
              </p>
              {/* <span className="progress-percentage">{Math.floor(progress)}%</span> */}
            </div>
            
            {waitingForModerator && (
              <div className="ai-analysis-notice">
                <i className="fas fa-brain"></i>
                <p>AI performing deep content analysis. This complex process may take some time.</p>
              </div>
            )}
            
            {waitingForBackendProcessing && (
              <div className="ai-analysis-notice">
                <i className="fas fa-cogs"></i>
                <p>Applying content moderation and finalizing video. This may take several minutes depending on video length.</p>
              </div>
            )}
            
            {showDetectedIssues && currentStep === 4 && (
              <div className="detected-issues">
                <div className="issues-header">
                  <i className="fas fa-exclamation-triangle"></i>
                  <h4>Content Issues Detected</h4>
                </div>
                <ul className="issues-list">
                  {detectedIssues.map((issue, index) => (
                    <li key={index} className={`issue-item issue-${issue.type.toLowerCase().replace(' ', '-')}`}>
                      <span className="issue-type">{issue.type}</span>
                      <span className="issue-time">{issue.timeRange}</span>
                    </li>
                  ))}
                </ul>
                <p className="issues-message">Applying appropriate content moderation...</p>
              </div>
            )}
          </div>
          
          <div className="step-list">
            {processingSteps.map((step, index) => {
              // Skip the "Content Issues Detected" step if not showing detected issues
              if (step.showAfterModeratorInput && !showDetectedIssues) {
                return null;
              }
              
              return (
                <div 
                  key={index} 
                  className={`step-item ${index < currentStep ? 'completed' : index === currentStep ? 'active' : ''}`}
                >
                  <div className="step-indicator">
                    {index < currentStep ? (
                      <i className="fas fa-check"></i>
                    ) : index === currentStep ? (
                      <i className="fas fa-circle-notch fa-spin"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="step-name">{step.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoProcessing; 